import { TrendingUp, TrendingDown, Minus, RefreshCw, Lock, AlertTriangle } from "lucide-react";
import SymbolAccuracyBadge from "@/components/SymbolAccuracyBadge";
import TradeProgress from "@/components/TradeProgress";
import { QuantAgreement } from "@/components/dashboard/QuantAgreement";
import { StrikeSuggest } from "@/components/dashboard/StrikeSuggest";
import { SignalAge } from "@/components/dashboard/SignalAge";
import { segmentOf } from "@/lib/segments";

function probabilityClass(p) {
  if (p >= 75) return "text-[#22C55E]";
  if (p >= 60) return "text-[#F59E0B]";
  return "text-[#EF4444]";
}

function NoTradeState({ signal, isPro, isAdmin }) {
  return (
    <div className="space-y-3" data-testid="ew-no-trade">
      <div className="border border-zinc-800 bg-[#0a0a0a] p-4">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[#22C55E] font-mono mb-2 flex items-center gap-1.5" data-testid="ew-failsafe-badge">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#22C55E] animate-pulse" />
          Failsafe mode on
        </div>
        <div className="font-heading text-base font-bold uppercase tracking-tight text-zinc-200 mb-2">No high probability trade available</div>
        <div className="text-xs text-zinc-400 leading-relaxed" data-testid="ew-no-trade-reason">
          {signal.reason}
        </div>
        {signal.cooldown_ends_at && (
          <div className="mt-3 text-[10px] uppercase tracking-widest font-mono text-zinc-500" data-testid="ew-cooldown">
            Cooldown ends: <span className="text-zinc-300">{new Date(signal.cooldown_ends_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
          </div>
        )}
        {signal.last_outcome && (isPro || isAdmin) && (
          <div className="mt-3 pt-3 border-t border-zinc-800 grid grid-cols-2 gap-2 text-[10px] font-mono" data-testid="ew-last-outcome">
            <div className="text-zinc-500 uppercase tracking-widest">Last trade</div>
            <div className={`text-right uppercase font-bold ${signal.last_outcome.status === "miss" ? "text-[#EF4444]" : signal.last_outcome.status?.startsWith("hit") ? "text-[#22C55E]" : "text-zinc-400"}`}>
              {signal.last_outcome.status?.replace("_", " · ")} ({signal.last_outcome.trend})
            </div>
            <div className="text-zinc-500">Entry → Closed</div>
            <div className="text-right text-zinc-300">
              {signal.last_outcome.entry?.toLocaleString("en-IN")} → {signal.last_outcome.closed_price?.toLocaleString("en-IN") || "—"}
            </div>
          </div>
        )}
      </div>
      {!isPro && !isAdmin && (
        <div className="border border-[#007AFF]/40 bg-[#007AFF]/10 p-3" data-testid="ew-no-trade-upgrade">
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#007AFF] mb-1 flex items-center gap-1.5">
            <Lock className="h-3 w-3" /> Pro members only
          </div>
          <div className="text-xs text-zinc-200 leading-relaxed">
            When the next high-confidence setup is detected, Pro subscribers see the full trade levels in real time.
          </div>
        </div>
      )}
    </div>
  );
}

function FreeLockedState({ upgrade }) {
  return (
    <div className="space-y-4" data-testid="ew-free-locked">
      {/* Free users see ONLY a teaser. No trend, no probability, no levels, no progress. */}
      <div className="flex items-center justify-between">
        <div className="text-xs font-mono uppercase text-zinc-700 flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Proprietary signal
        </div>
        <div className="text-xs font-mono uppercase text-zinc-700 flex items-center gap-1.5">
          <Lock className="h-3 w-3" /> Locked
        </div>
      </div>

      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Success probability</div>
        <div className="flex items-baseline gap-2 select-none">
          <span className="font-heading font-black text-5xl tracking-tighter text-zinc-800 blur-sm">██%</span>
          <span className="text-xs font-mono text-zinc-700 blur-sm">R:R █:█</span>
        </div>
        <div className="mt-2 h-1 bg-zinc-900 overflow-hidden">
          <div className="h-full bg-zinc-800" style={{ width: `40%` }} />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-zinc-900">
        {["Entry", "Stop Loss", "Target 1", "Target 2"].map((label) => (
          <div key={label} className="bg-[#121212] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
            <div className="font-mono font-bold text-base mt-1 text-zinc-800 blur-sm select-none">██,███.██</div>
          </div>
        ))}
      </div>

      <div className="border border-[#007AFF]/40 bg-[#007AFF]/10 p-4" data-testid="ew-free-upgrade">
        <div className="text-xs uppercase tracking-widest font-bold text-[#007AFF] mb-1 flex items-center gap-2">
          <Lock className="h-3 w-3" /> Pro members only
        </div>
        <div className="text-sm text-zinc-200 leading-relaxed mb-3">
          Full trade setup — direction, entry, stop-loss, targets, and live progress — is reserved for Pro subscribers. Start a 3-day free trial to unlock.
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => upgrade("indian_monthly")} className="border border-[#F59E0B]/50 hover:border-[#F59E0B] text-[11px] font-bold uppercase tracking-wider px-3 py-2" data-testid="ew-trial-monthly">Monthly → ₹2,999/mo</button>
          <button onClick={() => upgrade("indian_annual")} className="bg-[#007AFF] hover:bg-[#3395FF] text-[11px] font-bold uppercase tracking-wider px-3 py-2" data-testid="ew-trial-annual">Annual → ₹29,999/yr · 2 months free</button>
        </div>
      </div>
    </div>
  );
}

function DisciplineBadges({ signal }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-[10px] font-mono uppercase tracking-widest" data-testid="ew-discipline">
      {signal.htf_bias && (
        <span
          className={`px-2 py-0.5 border ${signal.htf_bias === "bullish" ? "border-[#22C55E]/40 text-[#22C55E]" : signal.htf_bias === "bearish" ? "border-[#EF4444]/40 text-[#EF4444]" : "border-zinc-700 text-zinc-500"}`}
          title="Higher-timeframe bias decided FIRST (1H + 15m). Only trade in this direction."
          data-testid="ew-htf-bias"
        >
          HTF · {signal.htf_bias}
        </span>
      )}
      {signal.sl_clamped && (
        <span
          className="px-2 py-0.5 border border-[#F59E0B]/40 text-[#F59E0B]"
          title={`Stop-loss auto-clamped to ${signal.sl_clamped_to} for capital discipline.`}
          data-testid="ew-sl-clamped"
        >
          SL clamped {signal.sl_clamped_to}
        </span>
      )}
      {signal.counter_trend_capped && (
        <span
          className="px-2 py-0.5 border border-[#A855F7]/40 text-[#A855F7]"
          title="5m setup goes against HTF bias — probability hard-capped at 60 (below 65% threshold)."
          data-testid="ew-counter-trend"
        >
          Counter-trend · capped 60%
        </span>
      )}
    </div>
  );
}

function ProSignalView({ signal, selected, isPro, isAdmin, alertsActive, strategy, candleState }) {
  const canAdj = typeof signal.entry_price === "number" && typeof signal.target_1 === "number" && typeof signal.stop_loss === "number";
  const isS2 = strategy === "s2" && canAdj;
  const isS1 = strategy === "s1" && canAdj && typeof signal.target_2 === "number";
  const r2 = (x) => Math.round(x * 100) / 100;
  const s1T1 = isS1 ? r2(signal.entry_price + 0.5 * (signal.target_1 - signal.entry_price)) : null;
  return (
    <div className="space-y-4">
      {/* Global-shock per-trade overlay — active when any AMBER/RED alert is live */}
      {alertsActive > 0 && (
        <div className="border border-[#F59E0B]/50 bg-[#F59E0B]/15 p-3" data-testid="ew-shock-overlay">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#F59E0B]">
            <AlertTriangle className="h-3.5 w-3.5 animate-pulse" /> Global shock active — review this trade
          </div>
          <div className="mt-1 text-xs text-zinc-300 leading-relaxed">
            A major international market is moving sharply right now (see banner above). Consider moving SL to break-even or closing this position — sudden cross-market correlation can blow through normal stop levels.
          </div>
        </div>
      )}

      {/* Tradeable threshold gate — surfaces an "AVOID" banner when probability < 65% */}
      {signal.meets_threshold === false && (
        <div className="border border-[#F59E0B]/40 bg-[#F59E0B]/10 p-3" data-testid="ew-below-threshold">
          <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-[#F59E0B]">
            <AlertTriangle className="h-3.5 w-3.5" /> Skip this setup — below {signal.min_probability_threshold || 65}% threshold
          </div>
          <div className="mt-1 text-xs text-zinc-300 leading-relaxed">
            Platform rule: do not take trades with a success probability below {signal.min_probability_threshold || 65}%. Levels are shown for context only — the verified track record above excludes these.
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        {signal.wave_count && isAdmin ? (
          <div className="text-xs font-mono uppercase text-zinc-500" data-testid="ew-wavecount">{signal.wave_count}</div>
        ) : (
          <div className="text-xs font-mono uppercase text-zinc-700 flex items-center gap-1.5" data-testid="ew-locked-wave">
            <Lock className="h-3 w-3" /> Proprietary signal
          </div>
        )}
        <div className={`text-xs uppercase font-bold tracking-widest flex items-center gap-1 ${signal.trend === "bullish" ? "text-[#22C55E]" : signal.trend === "bearish" ? "text-[#EF4444]" : "text-zinc-400"}`} data-testid="ew-trend">
          {signal.trend === "bullish" ? <TrendingUp className="h-3 w-3" /> : signal.trend === "bearish" ? <TrendingDown className="h-3 w-3" /> : <Minus className="h-3 w-3" />}
          {signal.trend}
        </div>
      </div>

      {/* Signal generation time + freshness — late viewers can judge entry validity */}
      {signal.generated_at && <SignalAge generatedAt={signal.generated_at} />}

      {/* Option strike bridge — INDIAN F&O only. Options data is meaningless for
          global instruments (forex/US stocks/crypto) — never fetch it for them. */}
      {(isPro || isAdmin) && signal.trend && signal.trend !== "neutral" &&
        segmentOf(selected?.symbol, selected?.category, selected?.tv_symbol) === "indian" && (
        <>
          <QuantAgreement symbol={selected?.symbol} direction={signal.trend} />
          <StrikeSuggest symbol={selected?.symbol} direction={signal.trend}
                         entry={signal.entry_price} stopLoss={signal.stop_loss}
                         target1={signal.target_1} target2={signal.target_2} />
        </>
      )}

      {/* HTF discipline indicators — admin sees the bias + any clamps that kicked in */}
      {isAdmin && (signal.htf_bias || signal.sl_clamped || signal.counter_trend_capped) && (
        <DisciplineBadges signal={signal} />
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-1">Success probability</div>
        <div className="flex items-baseline gap-2">
          <span className={`font-heading font-black text-5xl tracking-tighter ${probabilityClass(signal.success_probability)}`} data-testid="ew-probability">{signal.success_probability}%</span>
          <span className="text-xs font-mono text-zinc-500" data-testid="ew-rr">R:R {signal.risk_reward}</span>
          {typeof signal.risk_pct === "number" && (
            <span
              className={`text-xs font-mono ${signal.risk_pct > 1 ? "text-[#F59E0B]" : "text-zinc-500"}`}
              title={`Stop is ${signal.risk_pct}% from entry${signal.risk_pct_ceiling ? ` · ceiling for this asset class: ${signal.risk_pct_ceiling}%` : ""}`}
              data-testid="ew-risk-pct"
            >
              · SL {signal.risk_pct}%
            </span>
          )}
        </div>
        <div className="mt-2 h-1 bg-zinc-800 overflow-hidden">
          <div className={`h-full ${signal.success_probability >= 75 ? "bg-[#22C55E]" : signal.success_probability >= 60 ? "bg-[#F59E0B]" : "bg-[#EF4444]"}`} style={{ width: `${signal.success_probability}%` }} />
        </div>
        <div className="mt-3 flex items-center gap-3 text-[10px] uppercase tracking-widest font-mono text-zinc-500">
          <span className="px-2 py-0.5 border border-[#007AFF]/40 text-[#007AFF]" data-testid="ew-timeframe">{signal.timeframe || "5m"}</span>
          {signal.hold_time && <span data-testid="ew-hold">Hold ~ {signal.hold_time}</span>}
          {signal.data_quality && (
            <span
              className={`ml-auto px-2 py-0.5 border ${signal.data_quality === "live" ? "border-[#22C55E]/50 text-[#22C55E]" : "border-zinc-700 text-zinc-500"}`}
              title={signal.data_quality === "live" ? "Live 5m OHLC fed to the AI" : "AI reasoning from training-cutoff knowledge — no live feed wired for this asset class yet"}
              data-testid="ew-data-quality"
            >
              {signal.data_quality === "live" ? "● Live" : "○ Inferred"}
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-px bg-zinc-900">
        {[
          { l: "Entry", v: signal.entry_price, c: "text-white", tid: "ew-entry" },
          { l: isS2 ? "Stop · S2 (close)" : "Stop Loss", v: isS2 ? signal.entry_price : signal.stop_loss, c: "text-[#EF4444]", tid: "ew-stop" },
          { l: isS2 ? "Target · S2" : isS1 ? "Target · S1" : "Target 1", v: isS2 ? signal.stop_loss : isS1 ? s1T1 : signal.target_1, c: "text-[#22C55E]", tid: "ew-t1" },
          { l: isS2 ? "Target 2 · S2" : isS1 ? "Target 2 · S1" : "Target 2", v: (isS2 || isS1) ? "—" : signal.target_2, c: "text-[#22C55E]", tid: "ew-t2" },
        ].map((m) => (
          <div key={m.l} className="bg-[#121212] p-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500">{m.l}</div>
            <div className={`font-mono font-bold text-base mt-1 ${m.c}`} data-testid={m.tid}>{typeof m.v === "number" ? m.v.toLocaleString("en-IN", { maximumFractionDigits: 4 }) : m.v}</div>
          </div>
        ))}
      </div>

      {(isS1 || isS2) && (
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#A855F7]" data-testid="ew-strategy-note">
          {isS2 ? "Strategy 2 · opposite side (CE ↔ PE) · entry on 5-min close 5+ pts through entry"
            : "Strategy 1 · entry on 5-min close 5+ pts through entry · valid 30 min from signal"}
        </div>
      )}

      {(isS1 || isS2) && candleState && (
        <div className="text-[10px] font-mono uppercase tracking-widest" data-testid="ew-candle-state">
          {isS2 ? (
            candleState.s2_triggered
              ? <span className="px-2 py-1 border border-[#A855F7] text-[#A855F7] animate-pulse" data-testid="contra-triggered-badge">● CONTRA TRADE ON{typeof candleState.s2_stop === "number" ? ` · SL ${candleState.s2_stop}` : ""}</span>
              : candleState.decided === "s1"
                ? <span className="px-2 py-1 border border-zinc-700 text-zinc-500">CONTRA INVALID — ENTRY CONFIRMED FIRST</span>
                : <span className="px-2 py-1 border border-zinc-700 text-zinc-400">WAITING FOR TRIGGER CANDLE</span>
          ) : (
            candleState.s1_confirmed
              ? <span className="px-2 py-1 border border-[#22C55E] text-[#22C55E]" data-testid="entry-confirmed-badge">● ENTRY CONFIRMED</span>
              : candleState.s1_expired
                ? <span className="px-2 py-1 border border-[#EF4444]/60 text-[#EF4444]">ENTRY WINDOW EXPIRED</span>
                : candleState.decided === "s2"
                  ? <span className="px-2 py-1 border border-zinc-700 text-zinc-500">REJECTED — CONTRA CANDLE PRINTED</span>
                  : <span className="px-2 py-1 border border-zinc-700 text-zinc-400">AWAITING CONFIRMATION CANDLE</span>
          )}
        </div>
      )}

      {typeof signal.entry_price === "number" && typeof signal.stop_loss === "number" && signal.entry_price !== signal.stop_loss && (
        <div className="text-[11px] text-zinc-500 leading-relaxed border-l-2 border-[#F59E0B]/40 pl-3" data-testid="ew-sweep-zone">
          <span className="text-[#F59E0B] font-mono uppercase text-[10px] tracking-widest">Sweep risk · </span>
          Price may briefly probe to ~{(signal.stop_loss < signal.entry_price
            ? signal.stop_loss - 0.35 * Math.abs(signal.entry_price - signal.stop_loss)
            : signal.stop_loss + 0.35 * Math.abs(signal.entry_price - signal.stop_loss)
          ).toLocaleString("en-IN", { maximumFractionDigits: signal.entry_price >= 100 ? 2 : 4 })} before reversing — a quick wick past SL is a known stop-hunt pattern, not always a failed setup.
        </div>
      )}

      <TradeProgress signal={signal} />

      {/* Pro view: plain-English summary only. Admin view: full proprietary stack. */}
      {isAdmin && signal.rationale && (
        <div className="text-xs leading-relaxed text-zinc-400" data-testid="ew-rationale">
          {signal.rationale}
        </div>
      )}
      {!isAdmin && isPro && signal.pro_summary && (
        <div className="text-xs leading-relaxed text-zinc-300 border-l-2 border-[#007AFF]/40 pl-3" data-testid="ew-pro-summary">
          {signal.pro_summary}
        </div>
      )}

      {isAdmin && (signal.htf_alignment || signal.tech_confluence) && (
        <div className="space-y-2 pt-2 border-t border-zinc-900" data-testid="ew-confluence">
          {signal.htf_alignment && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#007AFF] mb-0.5">HTF alignment · 15m + 1h</div>
              <div className="text-[11px] leading-relaxed text-zinc-400">{signal.htf_alignment}</div>
            </div>
          )}
          {signal.tech_confluence && (
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#007AFF] mb-0.5">Tech confluence · RSI · MACD · EMA · BB · VWAP</div>
              <div className="text-[11px] leading-relaxed text-zinc-400">{signal.tech_confluence}</div>
            </div>
          )}
        </div>
      )}

      {signal.ensemble && signal.ensemble.agreement && (
        <div className="flex items-center gap-2 pt-2 text-[10px] font-mono uppercase tracking-widest" data-testid="ew-ensemble">
          <span className="text-zinc-500">AI confluence</span>
          <span className={`px-2 py-0.5 border ${signal.ensemble.agreement === "3/3" ? "border-[#22C55E]/50 text-[#22C55E]" : signal.ensemble.agreement === "2/3" ? "border-[#F59E0B]/50 text-[#F59E0B]" : "border-zinc-700 text-zinc-500"}`}>
            {signal.ensemble.agreement} models agree
          </span>
          {isAdmin && signal.ensemble.models_used?.length > 0 && (
            <span className="text-zinc-600 truncate">{signal.ensemble.models_used.map(m => m.split(":")[0]).join(" · ")}</span>
          )}
        </div>
      )}

      {isAdmin && signal.key_levels?.length > 0 && (
        <div className="pt-2" data-testid="ew-keylevels">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#007AFF] mb-1">Key levels</div>
          <ul className="space-y-0.5 text-[11px] text-zinc-400">
            {signal.key_levels.map((k) => {
              const label = typeof k === "string" ? k : JSON.stringify(k);
              return <li key={label} className="font-mono">{label}</li>;
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export const SignalPanel = ({ signal, loadingSig, loadingMsg, selected, mode, strategy, candleState, isPro, isAdmin, alertsActive, onRefresh, upgrade }) => {
  return (
    <div className="bg-[#121212] border border-zinc-800 p-4" data-testid="ew-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-zinc-500">AI Signal</div>
          <div className="font-heading text-lg font-bold uppercase tracking-tight">Trade Setup</div>
        </div>
        <div className="flex items-center gap-2">
          {(isPro || isAdmin) && selected?.symbol && (
            <SymbolAccuracyBadge symbol={selected.symbol} mode={mode} />
          )}
          {/* Refresh is admin-only — Pro users get auto-managed signals (saves LLM credits + prevents over-trading) */}
          {isAdmin && (
            <button onClick={onRefresh} className="text-zinc-500 hover:text-white p-1" data-testid="ew-refresh">
              <RefreshCw className={`h-4 w-4 ${loadingSig ? "animate-spin" : ""}`} />
            </button>
          )}
        </div>
      </div>

      {loadingSig && <div className="text-xs font-mono text-zinc-500 uppercase tracking-widest py-8 text-center" data-testid="ew-loading">◢ {loadingMsg}</div>}

      {signal && !loadingSig && signal.status === "no_trade" && (
        <NoTradeState signal={signal} isPro={isPro} isAdmin={isAdmin} />
      )}

      {signal && !loadingSig && signal.status !== "no_trade" && !isPro && !isAdmin && (
        <FreeLockedState upgrade={upgrade} />
      )}

      {signal && !loadingSig && signal.status !== "no_trade" && (isPro || isAdmin) && (
        <ProSignalView signal={signal} selected={selected} isPro={isPro} isAdmin={isAdmin} alertsActive={alertsActive} strategy={strategy} candleState={candleState} />
      )}
    </div>
  );
};
